
import { useCallback, useEffect, useRef, useState } from 'react';
import { Film, ImageIcon, TriangleAlert } from 'lucide-react';

import { Pill } from '../../components/ui';
import { insertResolution } from '../../hooks';
import type { Insert } from '../../types/api';
import { formatDuration, metaLine, untaggedNote } from './format';
import styles from './LibraryPage.module.css';

const POSTER_AT_SECONDS = 0.6;

interface Props {
  item: Insert;
  selected: boolean;
  onOpen: (item: Insert) => void;
}

export function InsertCard({ item, selected, onOpen }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [hovered, setHovered] = useState(false);
  const [failed, setFailed] = useState(false);

  const isVideo = item.media_type === 'video';
  const url = item.media?.url ?? null;
  const untagged = untaggedNote(item);
  const duration = formatDuration(item.duration_ms);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideo || !url) return;
    if (hovered) {
      video.currentTime = 0;
      void video.play().catch(() => {
      });
      return () => video.pause();
    }
    video.pause();
    try {
      video.currentTime = POSTER_AT_SECONDS;
    } catch {
      return;
    }
    return undefined;
  }, [hovered, isVideo, url]);

  const open = useCallback(() => onOpen(item), [onOpen, item]);

  return (
    <article
      className={`${styles.card} ${selected ? styles.cardSelected : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={`${styles.frame} ${untagged ? styles.frameRaw : ''}`}>
        {url && isVideo && !failed && (
          <video
            ref={videoRef}
            className={styles.frameMedia}
            src={`${url}#t=${POSTER_AT_SECONDS}`}
            preload="metadata"
            muted
            loop
            playsInline
            onError={() => setFailed(true)}
          />
        )}
        {url && !isVideo && !failed && (
          <img
            className={styles.frameMedia}
            src={url}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setFailed(true)}
          />
        )}
        {(!url || failed) &&
          (isVideo ? (
            <Film size={24} strokeWidth={1.2} className={styles.framePlaceholder} />
          ) : (
            <ImageIcon size={24} strokeWidth={1.2} className={styles.framePlaceholder} />
          ))}

        <span className={isVideo ? styles.typeBadgeVideo : styles.typeBadgePhoto}>
          {isVideo ? 'видео' : 'фото'}
        </span>
        {duration && <span className={styles.durationBadge}>{duration}</span>}

        {untagged && (
          <span className={styles.rawBadge} title={untagged}>
            <TriangleAlert size={11} strokeWidth={1.8} />
            не размечен
          </span>
        )}
        {failed && url && <span className={styles.frameNote}>превью не открылось</span>}
      </div>

      <div className={styles.cardBody}>
        <div className={styles.cardTitle}>{item.title}</div>

        {(item.tags.length > 0 || item.mood.length > 0) && (
          <div className={styles.pillRow}>
            {item.tags.slice(0, 4).map((tag) => (
              <Pill key={`t-${tag}`} className={styles.pillClamp}>
                {tag}
              </Pill>
            ))}
            {item.mood.slice(0, 2).map((mood) => (
              <Pill key={`m-${mood}`} className={`${styles.pillClamp} ${styles.pillMood}`}>
                {mood}
              </Pill>
            ))}
            {item.tags.length > 4 && (
              <Pill className={`${styles.pillClamp} ${styles.pillMore}`}>
                +{item.tags.length - 4}
              </Pill>
            )}
          </div>
        )}

        <div className={styles.cardMeta} title={metaLine(item)}>
          {insertResolution(item) ? `${insertResolution(item)} · ` : ''}
          {metaLine(item)}
        </div>
      </div>

      <button
        type="button"
        className={styles.cardOpen}
        onClick={open}
        aria-label={`Открыть «${item.title}»`}
      />
    </article>
  );
}
